import { useRef, useState } from "react";
import { Upload, Loader2, ShieldCheck, RefreshCw, Lightbulb, Info } from "lucide-react";
import { SliderField } from "@/components/SliderField";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { processPhoto, releasePhoto } from "@/lib/photo";
import { PHOTO_KEYS, getTemplate } from "@/lib/poster";

const PhotoSlot = ({ slot, photo, onPhoto, mode, scale, onScale, off, onOff }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const onFile = async (file) => {
    if (!file) return;
    if (file.type && !file.type.startsWith("image/")) return toast.error("Merci d'importer une image (JPG, PNG…)");
    setBusy(true);
    setProgress(0);
    try {
      const next = await processPhoto(file, (_, p) => setProgress(Math.round(p * 100)));
      onPhoto(next);
      if (photo) setTimeout(() => releasePhoto(photo), 500);
      toast.success(`${slot.label} : arrière-plan supprimé`);
    } catch (e) {
      console.error("Background removal failed", e);
      toast.error(e?.message === "FORMAT"
        ? "Format de photo non pris en charge (HEIC…). Exportez-la en JPG ou PNG puis réessayez."
        : "Le détourage a échoué. Vérifiez votre connexion internet puis réessayez, ou essayez une autre photo.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid={`photo-slot-${slot.key}`}>
      <span className="label-xs">{slot.label}</span>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} data-testid={`photo-upload-input-${slot.key}`} />
      {busy ? (
        <div className="rounded-2xl border border-[#DDD3C6] bg-white p-4 h-40 flex flex-col justify-center gap-3" data-testid={`photo-processing-${slot.key}`}>
          <div className="flex items-center gap-2 text-xs text-[#3A322B]"><Loader2 className="animate-spin text-[#7B5338]" size={16} /> Détourage…</div>
          <Progress value={progress} className="h-1.5" />
        </div>
      ) : photo ? (
        <button type="button" onClick={() => inputRef.current.click()} className="checker group relative w-full h-40 rounded-2xl border border-[#DDD3C6] flex items-center justify-center overflow-hidden" data-testid={`photo-change-${slot.key}`}>
          <img src={photo.previewUrl} alt={slot.label} className={`max-w-full max-h-full ${mode === "bw" ? "grayscale" : ""}`} data-testid={`photo-cutout-thumbnail-${slot.key}`} />
          <span className="absolute bottom-2 right-2 bg-white/90 rounded-full px-3 py-1 text-xs flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"><RefreshCw size={12} /> Changer</span>
        </button>
      ) : (
        <button type="button" onClick={() => inputRef.current.click()} data-testid={`photo-upload-dropzone-${slot.key}`}
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files[0]); }}
          className="w-full h-40 rounded-2xl border-2 border-dashed border-[#CDC0B0] bg-white/60 hover:border-[#7B5338] hover:bg-white transition-colors duration-200 flex flex-col items-center justify-center gap-2 text-center p-3">
          <Upload className="text-[#7B5338]" size={20} />
          <span className="text-sm text-[#3A322B]">Importer</span>
          <span className="text-[11px] text-[#9A8C7D]">JPG, PNG — glisser-déposer</span>
        </button>
      )}
      {photo && (
        <div className="space-y-3 rounded-xl bg-[#EADFD3]/70 p-3">
          <SliderField label="Taille" value={scale} min={20} max={200} onChange={onScale} testId={`photo-size-${slot.key}`} />
          <SliderField label="Gauche / droite" value={off.dx || 0} min={-40} max={40} step={0.5} format={signed} onChange={(v) => onOff({ ...off, dx: v })} testId={`photo-dx-${slot.key}`} />
          <SliderField label="Haut / bas" value={off.dy || 0} min={-50} max={30} step={0.5} format={signed} onChange={(v) => onOff({ ...off, dy: v })} testId={`photo-dy-${slot.key}`} />
        </div>
      )}
    </div>
  );
};

const signed = (v) => `${v > 0 ? "+" : ""}${v} %`;

const Toggle = ({ options, value, onChange, testPrefix }) => (
  <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-[#EADFD3]">
    {options.map((o) => (
      <button key={o.id} type="button" onClick={() => onChange(o.id)} data-testid={`${testPrefix}-${o.id}`}
        className={`rounded-full py-2 text-sm transition-colors duration-200 ${value === o.id ? "bg-white text-[#3A322B] shadow-sm" : "text-[#6B5F53]"}`}>{o.label}</button>
    ))}
  </div>
);

export const PhotoStep = ({ photos, setPhotos, cfg, set }) => {
  const tpl = getTemplate(cfg.templateId);
  const count = cfg.photoCount || 2;
  const slots = PHOTO_KEYS.slice(0, count).map((key, i) => ({ key, label: count === 1 ? tpl.singleLabel || tpl.photoLabels[0] : tpl.photoLabels[i] }));
  return (
  <div className="space-y-5">
    {tpl.defaults.photoCountChoice && (
      <div className="space-y-2"><span className="label-xs">Nombre de photos</span>
        <Toggle testPrefix="photo-count" value={String(count)} onChange={(v) => set({ photoCount: Number(v) })} options={[{ id: "1", label: "1 photo" }, { id: "2", label: "2 photos" }]} /></div>
    )}
    <div className="rounded-2xl bg-[#EADFD3] border border-[#DDD3C6] p-4 space-y-2" data-testid="photo-tips">
      <p className="flex items-center gap-2 text-sm font-medium text-[#3A322B]"><Lightbulb size={16} className="text-[#7B5338]" /> Pour un détourage parfait</p>
      <ul className="text-xs text-[#6B5F53] space-y-1 list-disc pl-5 leading-relaxed">
        <li>Importez la photo dans l'emplacement prévu ci-dessous ({slots.map((s) => s.label).join(" / ")}).</li>
        <li>Choisissez une photo où la personne se détache bien d'un fond simple et uni (mur clair, ciel…).</li>
        <li>Bonne lumière, photo nette et en haute qualité (évitez les captures d'écran et photos floues).</li>
        <li>Une seule personne par photo, entière ou jusqu'à la taille, sans objet ni personne devant elle.</li>
        <li>Évitez les fonds chargés ou de la même couleur que les vêtements ou les cheveux.</li>
      </ul>
    </div>
    <div className="rounded-2xl bg-white border border-[#C2B4A2] p-4 flex gap-3" data-testid="photo-no-effects-notice">
      <Info size={18} className="text-[#7B5338] shrink-0 mt-0.5" />
      <p className="text-xs text-[#3A322B] leading-relaxed">
        <strong>Vos photos sont importées telles que vous les insérez, sans aucun effet ni retouche.</strong> Si vous souhaitez une photo avec effet (câlin, pose ensemble, modification…), générez-la au préalable avec ChatGPT ou une autre IA, puis importez ici la photo obtenue.
      </p>
    </div>
    <p className="text-sm text-[#6B5F53]">{count === 2 ? "Chacun importe sa photo : elles sont détourées puis réunies côte à côte sur le tableau." : "Importez votre photo : elle est détourée puis placée au centre du tableau."}</p>
    <div className={`grid gap-3 ${count === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
      {slots.map((s) => (
        <PhotoSlot key={s.key} slot={s} photo={photos[s.key]} mode={cfg.photoMode}
          scale={cfg.photoScale?.[s.key] ?? 100} onScale={(v) => set({ photoScale: { ...cfg.photoScale, [s.key]: v } })}
          off={cfg.photoOffset?.[s.key] || {}} onOff={(v) => set({ photoOffset: { ...cfg.photoOffset, [s.key]: v } })} onPhoto={(p) => setPhotos((ps) => ({ ...ps, [s.key]: p }))} />
      ))}
    </div>
    <div className="space-y-2"><span className="label-xs">Rendu des photos</span>
      <Toggle testPrefix="photo-mode" value={cfg.photoMode} onChange={(v) => set({ photoMode: v })} options={[{ id: "color", label: "Couleur" }, { id: "bw", label: "Noir & blanc" }]} /></div>
    {count === 2 && (
      <div className="space-y-2"><span className="label-xs">Placement</span>
        <Toggle testPrefix="photo-order" value={cfg.photoOrder} onChange={(v) => set({ photoOrder: v })}
          options={[{ id: "groom-left", label: `${tpl.photoShort[0]} à gauche` }, { id: "bride-left", label: `${tpl.photoShort[1]} à gauche` }]} /></div>
    )}
    {count === 2 && (
      <div className="space-y-2"><span className="label-xs">Photo au premier plan (si elles se superposent)</span>
        <Toggle testPrefix="photo-front" value={cfg.photoFront || "bride"} onChange={(v) => set({ photoFront: v })}
          options={[{ id: "groom", label: tpl.photoShort[0] }, { id: "bride", label: tpl.photoShort[1] }]} /></div>
    )}
    <p className="flex gap-2 text-xs text-[#6B5F53] leading-relaxed">
      <ShieldCheck size={16} className="text-[#8A9A86] shrink-0" />
      Les photos restent identiques aux originales : seul l'arrière-plan est supprimé. Aucune retouche, aucune déformation des personnes.
    </p>
  </div>
  );
};
